import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ZoomIn } from 'react-feather';

export const buildSecureMediaUrl = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  if (path.startsWith('/api/')) {
    return `${baseUrl}${path}`;
  }
  if (path.startsWith('/')) {
    if (path.startsWith('/uploads/')) {
      return `${API_BASE_URL}${path}`;
    }
    return `${baseUrl}${path}`;
  }
  return `${API_BASE_URL}/uploads/${path}`;
};

interface SecureImageProps {
  srcPath: string | null | undefined;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  borderRadius?: number;
  fixedWidth?: number;
  fixedHeight?: number;
  showLoader?: boolean;
  showZoomHint?: boolean;
  onLoad?: () => void;
}

const SecureImage: React.FC<SecureImageProps> = ({
  srcPath,
  alt = '',
  className,
  style,
  borderRadius = 4,
  fixedWidth,
  fixedHeight,
  showLoader = false,
  showZoomHint = false,
  onLoad,
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const hasFixedFrame = fixedWidth != null && fixedHeight != null;

  useEffect(() => {
    setImageLoaded(false);
    if (!srcPath || !srcPath.trim()) {
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setResolvedSrc(null);
      return;
    }

    let cancelled = false;

    const revokeBlob = () => {
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = null;
    };

    const load = async () => {
      revokeBlob();
      setResolvedSrc(null);

      const url = buildSecureMediaUrl(srcPath);

      const directOk = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (!cancelled) setResolvedSrc(url);
          resolve(true);
        };
        img.onerror = () => resolve(false);
        img.src = url;
      });

      if (cancelled || directOk) return;

      try {
        const token = localStorage.getItem('student_token');
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (!response.ok) {
          console.error('❌ Failed to load image:', response.status);
          return;
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setResolvedSrc(blobUrl);
      } catch (e) {
        if (!cancelled) {
          console.error('❌ Error loading image:', e);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [srcPath]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    onLoad?.();
  };

  const imageElement = resolvedSrc ? (
    <Box
      component="img"
      src={resolvedSrc}
      alt={alt}
      onLoad={handleImageLoad}
      onError={() => setImageLoaded(true)}
      sx={
        hasFixedFrame
          ? {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.15s ease',
            }
          : {
              maxWidth: '100%',
              height: 'auto',
              borderRadius,
              display: 'block',
              ...style,
            }
      }
    />
  ) : null;

  if (hasFixedFrame) {
    return (
      <Box
        className={`secure-image secure-image--fixed ${className || ''}`.trim()}
        sx={{
          width: fixedWidth,
          maxWidth: '100%',
          height: fixedHeight,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${borderRadius}px`,
          bgcolor: '#e8ecea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {showLoader && !imageLoaded && (
          <CircularProgress size={22} sx={{ color: 'var(--brand-shell-bg, #13322B)' }} />
        )}
        {imageElement}
        {showZoomHint && imageLoaded && (
          <Box
            className="secure-image__zoom-hint"
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              opacity: 0,
              transition: 'opacity 0.15s ease',
              pointerEvents: 'none',
            }}
          >
            <ZoomIn size={28} color="#fff" strokeWidth={2} />
          </Box>
        )}
      </Box>
    );
  }

  if (!resolvedSrc) return null;

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      onLoad={handleImageLoad}
      style={{
        maxWidth: '100%',
        height: 'auto',
        borderRadius,
        display: 'block',
        ...style,
      }}
    />
  );
};

export default SecureImage;
